import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Download, ArrowRight, Loader2, AlertCircle } from 'lucide-react';

import SEO from '../components/SEO';
import axios from 'axios';

const storeApi = axios.create({
  baseURL: (import.meta.env.VITE_API_URL || 'https://residualvault.com/api/v1').replace('/api/v1', '/api/store'),
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
});

interface PurchaseInfo {
  product_key: string;
  product_name: string;
  status: string;
  download_url?: string;
}

interface UpsellProduct {
  product_key: string;
  name: string;
  description: string;
  price_cents: number;
}

export default function StoreSuccessPage() {
  const [searchParams] = useSearchParams();
  const [purchase, setPurchase] = useState<PurchaseInfo | null>(null);
  const [upsell, setUpsell] = useState<UpsellProduct | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) { setError('No session ID'); setLoading(false); return; }

    const poll = async (attempts: number) => {
      try {
        const res = await storeApi.get(`/success?session_id=${sessionId}`);
        const data = res.data;
        setPurchase(data.purchase);
        if (data.upsell_product) setUpsell(data.upsell_product);
        if (data.purchase.status === 'completed') {
          setLoading(false);
          return;
        }
        if (attempts < 10) {
          setTimeout(() => poll(attempts + 1), 2000);
        } else {
          setLoading(false);
        }
      } catch {
        if (attempts < 5) {
          setTimeout(() => poll(attempts + 1), 2000);
        } else {
          setError('Failed to verify purchase');
          setLoading(false);
        }
      }
    };

    poll(0);
  }, [sessionId]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Something went wrong</h1>
          <p className="text-slate-400 mb-6">{error}</p>
          <Link to="/store" className="text-sky-400 hover:text-sky-300">Back to Store</Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-sky-400 mx-auto mb-4 animate-spin" />
          <h2 className="text-xl font-bold text-white mb-2">Confirming your purchase...</h2>
          <p className="text-slate-400">This usually takes just a moment.</p>
        </div>
      </div>
    );
  }

  const downloadBase = (import.meta.env.VITE_API_URL || 'https://residualvault.com/api/v1').replace('/api/v1', '');

  return (
    <>
      <SEO title="Purchase Complete — ResidualVault" description="Your ResidualVault purchase is complete." canonical="https://residualvault.com/store/success" />
      <div className="min-h-screen flex items-center justify-center px-4 py-20">
        <div className="max-w-lg w-full">
          <div className="text-center mb-8">
            <CheckCircle className="w-20 h-20 text-emerald-400 mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-white mb-2">Purchase Complete!</h1>
            <p className="text-slate-400">Thank you for your purchase.</p>
          </div>

          <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-8 mb-8">
            <h2 className="text-lg font-semibold text-white mb-2">{purchase?.product_name}</h2>
            <p className="text-slate-400 text-sm mb-6">Your download link has also been sent to your email.</p>

            {purchase?.download_url && (
              <a
                href={`${downloadBase}${purchase.download_url}`}
                className="flex items-center justify-center gap-2 w-full py-4 px-6 bg-gradient-to-r from-emerald-500 to-sky-500 text-white rounded-xl font-bold text-lg hover:shadow-lg hover:shadow-emerald-500/25 transition-all"
              >
                <Download className="w-5 h-5" />
                Download Now
              </a>
            )}

            <p className="text-slate-500 text-xs text-center mt-4">
              Link valid for 30 days. Up to 5 downloads.
            </p>
          </div>

          {upsell && (
            <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 border border-sky-500/30 rounded-2xl p-6 mb-8">
              <p className="text-sky-400 text-sm font-medium mb-2">Complete your education</p>
              <h3 className="text-lg font-bold text-white mb-2">{upsell.name}</h3>
              <p className="text-slate-400 text-sm mb-4">{upsell.description}</p>
              <Link
                to="/store"
                className="inline-flex items-center gap-2 text-sky-400 hover:text-sky-300 font-medium"
              >
                View in Store <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}

          <div className="text-center">
            <Link to="/store" className="text-slate-400 hover:text-sky-400 transition-colors text-sm">
              Back to Store
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
